import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n-dict";
import { IsoPlate, IsoSeam, IsoStep } from "./Iso";
import { Reveal } from "./Reveal";

export function Section({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn("relative py-20 md:py-28", className)}>
      <IsoSeam className="absolute inset-x-0 top-0" />
      <div className="container-page">{children}</div>
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  lead,
  align = "start",
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  align?: "start" | "center";
}) {
  const t = useT();
  return (
    <Reveal
      className={cn("max-w-3xl", align === "center" && "mx-auto text-center")}
    >
      {eyebrow ? (
        <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
          {t(eyebrow)}
        </p>
      ) : null}
      <h2 className="mt-3 font-display text-3xl font-bold text-balance-tight md:text-4xl">
        {t(title)}
      </h2>
      {lead ? (
        <p className="mt-4 text-base text-muted-foreground md:text-lg">{t(lead)}</p>
      ) : null}
    </Reveal>
  );
}

/** Alternating text/visual band — the site's core layout unit. */
export function ZigzagRow({
  index,
  eyebrow,
  kicker,
  title,
  body,
  items,
  visual,
  action,
}: {
  index: number;
  eyebrow?: string;
  kicker?: string | undefined;
  title: string;
  body?: string;
  items?: string[];
  visual: ReactNode;
  action?: ReactNode;
}) {
  const t = useT();
  const flipped = index % 2 === 1;
  return (
    <div className="grid items-center gap-10 py-12 md:grid-cols-2 md:gap-16 md:py-16">
      <Reveal className={cn(flipped && "md:order-2")}>
        {eyebrow ? <IsoStep n={t(eyebrow)} className="mb-1" /> : null}
        {kicker ? (
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
            {t(kicker)}
          </p>
        ) : null}
        <h3 className="mt-3 font-display text-2xl font-bold text-balance-tight md:text-3xl">
          {t(title)}
        </h3>
        {body ? <p className="mt-4 text-muted-foreground">{t(body)}</p> : null}
        {items?.length ? (
          <ul className="feature-bullet-list mt-6 grid gap-2.5 sm:grid-cols-2">
            {items.map((item) => (
              <li key={item} className="flex gap-2.5 text-base text-muted-foreground md:text-lg">
                <span
                  aria-hidden
                  className="mt-2.5 size-2 shrink-0 skew-y-[-20deg] bg-primary"
                />
                {t(item)}
              </li>
            ))}
          </ul>
        ) : null}
        {action ? <div className="mt-8">{action}</div> : null}
      </Reveal>
      <Reveal variant="iso" delay={80} className={cn(flipped && "md:order-1")}>
        {visual}
      </Reveal>
    </div>
  );
}

/** Thin wrapper around IsoPlate so framed media shares the isometric token set. */
export function Frame({ children, className }: { children: ReactNode; className?: string }) {
  return <IsoPlate className={cn("rounded-xl p-3", className)}>{children}</IsoPlate>;
}

